/**
 * Query key factory — centralized query keys for React Query.
 * Pattern: queryKeys.[entity].list(filters?) | detail(id) | all
 *
 * This ensures consistent cache invalidation across the app.
 */

export const queryKeys = {
  auth: {
    me: ['auth', 'me'] as const,
  },

  projects: {
    all: ['projects'] as const,
    list: (filters?: Record<string, unknown>) => ['projects', 'list', filters] as const,
    detail: (id: string) => ['projects', 'detail', id] as const,
  },

  clients: {
    all: ['clients'] as const,
    list: (filters?: Record<string, unknown>) => ['clients', 'list', filters] as const,
    detail: (id: string) => ['clients', 'detail', id] as const,
  },

  proformas: {
    all: ['proformas'] as const,
    list: (filters?: Record<string, unknown>) => ['proformas', 'list', filters] as const,
    detail: (id: string) => ['proformas', 'detail', id] as const,
    items: (proformaId: string) => ['proformas', 'items', proformaId] as const,
  },

  planillas: {
    all: ['planillas'] as const,
    list: (filters?: Record<string, unknown>) => ['planillas', 'list', filters] as const,
    detail: (id: string) => ['planillas', 'detail', id] as const,
  },

  maestros: {
    all: ['maestros'] as const,
    list: (filters?: Record<string, unknown>) => ['maestros', 'list', filters] as const,
    detail: (id: string) => ['maestros', 'detail', id] as const,
  },

  projectMaestros: {
    all: ['project-maestros'] as const,
    list: (projectId: string) => ['project-maestros', 'list', projectId] as const,
  },

  recipes: {
    all: ['recipes'] as const,
    list: (filters?: Record<string, unknown>) => ['recipes', 'list', filters] as const,
    detail: (id: string) => ['recipes', 'detail', id] as const,
  },

  materials: {
    all: ['materials'] as const,
    list: (filters?: Record<string, unknown>) => ['materials', 'list', filters] as const,
    detail: (id: string) => ['materials', 'detail', id] as const,
  },

  ordenesTrabajo: {
    all: ['ordenes-trabajo'] as const,
    list: (filters?: Record<string, unknown>) => ['ordenes-trabajo', 'list', filters] as const,
    detail: (id: string) => ['ordenes-trabajo', 'detail', id] as const,
  },

  pedidos: {
    all: ['pedidos'] as const,
    list: (filters?: Record<string, unknown>) => ['pedidos', 'list', filters] as const,
    detail: (id: string) => ['pedidos', 'detail', id] as const,
  },

  despachos: {
    all: ['despachos'] as const,
    list: (filters?: Record<string, unknown>) => ['despachos', 'list', filters] as const,
    detail: (id: string) => ['despachos', 'detail', id] as const,
  },

  equipos: {
    all: ['equipos'] as const,
    list: (filters?: Record<string, unknown>) => ['equipos', 'list', filters] as const,
  },

  anticipos: {
    all: ['anticipos'] as const,
    list: (projectId: string) => ['anticipos', 'list', projectId] as const,
  },

  notifications: {
    all: ['notifications'] as const,
    unread: ['notifications', 'unread'] as const,
    list: (filters?: Record<string, unknown>) => ['notifications', 'list', filters] as const,
  },

  users: {
    all: ['users'] as const,
    list: (filters?: Record<string, unknown>) => ['users', 'list', filters] as const,
  },

  dashboard: {
    summary: ['dashboard', 'summary'] as const,
  },

  projectSummary: {
    detail: (projectId: string) => ['project-summary', projectId] as const,
  },

  medidas: {
    all: ['medidas'] as const,
    byProject: (projectId: string) => ['medidas', 'list', projectId] as const,
    list: (projectId: string, filters?: Record<string, unknown>) =>
      ['medidas', 'list', projectId, filters] as const,
  },

  materialBalance: {
    all: ['material-balance'] as const,
    byProject: (projectId: string) => ['material-balance', projectId] as const,
  },

  avance: {
    financiero: (projectId: string) => ['avance', 'financiero', projectId] as const,
    fisico: (projectId: string) => ['avance', 'fisico', projectId] as const,
  },

  medidaSets: {
    all: ['medida-sets'] as const,
    list: (projectId: string) => ['medida-sets', 'list', projectId] as const,
  },

  contabilidad: {
    reporte: (desde: string, hasta: string) => ['contabilidad', 'reporte', desde, hasta] as const,
  },

  cabeceras: {
    all: ['cabeceras'] as const,
    byProject: (projectId: string) => ['cabeceras', 'list', projectId] as const,
  },

  planillaHistorial: {
    all: ['planilla-historial'] as const,
    byProject: (projectId: string, filters?: Record<string, unknown>) =>
      ['planilla-historial', projectId, filters] as const,
  },

  insumos: {
    all: ['insumos'] as const,
    list: (filters?: Record<string, unknown>) => ['insumos', 'list', filters] as const,
    detail: (id: string) => ['insumos', 'detail', id] as const,
  },
};
